// File: HomeGallery.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './Homegallery.css'; // The corresponding CSS file
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronLeft, faChevronRight, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
// We no longer need react-responsive-masonry
// import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

const HomeGallery = () => {
    const [allImages, setAllImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All');

    const [imagesToDisplay, setImagesToDisplay] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [imagesPerPage, setImagesPerPage] = useState(12);

    const [modalImageIndex, setModalImageIndex] = useState(null);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.hitkarihospital.com:5005';

    const fetchImages = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/gallery-images`);
            const fetchedImages = response.data;
            
            if (!Array.isArray(fetchedImages)) {
                throw new Error("Data received from API is not an array.");
            }

            const imagesWithCategory = fetchedImages.map(img => ({
                ...img,
                category: img.heading || 'Uncategorized'
            }));

            setAllImages(imagesWithCategory);

            const uniqueCategories = ['All', ...new Set(imagesWithCategory.map(img => img.category))].sort((a, b) => {
                if (a === 'All') return -1;
                if (b === 'All') return 1;
                return a.localeCompare(b);
            });
            setCategories(uniqueCategories);

        } catch (err) {
            setError('Failed to fetch images. Please check your connection and ensure the backend is running.');
            console.error("Fetch error:", err);
            setAllImages([]);
            setCategories(['All']);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL]);

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    useEffect(() => {
        let filtered = allImages;
        if (selectedCategory !== 'All') {
            filtered = filtered.filter(image => image.category === selectedCategory);
        }

        const indexOfLastImage = currentPage * imagesPerPage;
        const indexOfFirstImage = indexOfLastImage - imagesPerPage;
        setImagesToDisplay(filtered.slice(indexOfFirstImage, indexOfLastImage));

        if (Math.ceil(filtered.length / imagesPerPage) < currentPage && filtered.length > 0) {
            setCurrentPage(1);
        } else if (filtered.length === 0) {
            setCurrentPage(1);
        }
    }, [allImages, selectedCategory, currentPage, imagesPerPage]);


    const totalPages = useMemo(() => {
        const source = (selectedCategory === 'All')
            ? allImages
            : allImages.filter(image => image.category === selectedCategory);
        return Math.ceil(source.length / imagesPerPage) || 1;
    }, [allImages, selectedCategory, imagesPerPage]);

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setCurrentPage(1);
    };

    const handleImagesPerPageChange = (e) => {
        setImagesPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const openModal = (indexInDisplayed) => {
        setModalImageIndex(indexInDisplayed);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = useCallback(() => {
        setModalImageIndex(null);
        document.body.style.overflow = 'auto';
    }, []);

    const showNextImage = useCallback(() => {
        if (modalImageIndex === null) return;
        setModalImageIndex((prevIndex) => (prevIndex + 1) % imagesToDisplay.length);
    }, [modalImageIndex, imagesToDisplay.length]);

    const showPrevImage = useCallback(() => {
        if (modalImageIndex === null) return;
        setModalImageIndex((prevIndex) => (prevIndex - 1 + imagesToDisplay.length) % imagesToDisplay.length);
    }, [modalImageIndex, imagesToDisplay.length]);
    
    // The rest of the logic remains unchanged...

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (modalImageIndex !== null) {
                if (event.key === 'Escape') closeModal();
                if (event.key === 'ArrowRight') showNextImage();
                if (event.key === 'ArrowLeft') showPrevImage();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [modalImageIndex, closeModal, showNextImage, showPrevImage]);

    const getPageNumbers = () => {
        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const pages = [];
        if (currentPage <= 3) {
            pages.push(1, 2, 3, '...', totalPages);
        } else if (currentPage > totalPages - 3) {
            pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
        } else {
            pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
        }
        return pages;
    };


    if (loading) {
        return (
            <div className="home-gallery-loader-container">
                <div className="home-gallery-spinner"></div>
                <p>Loading Gallery...</p>
            </div>
        );
    }

    return (
        <div className="home-gallery-page">
            <div className="home-gallery-container">
                {error && <div className="home-gallery-error-message">{error}</div>}
                <div className="home-gallery-controls-wrapper">
                    {/* Controls remain the same */}
                    <div className="home-gallery-category-filter">
                        <FontAwesomeIcon icon={faFolderOpen} className="home-gallery-filter-icon" />
                        {categories.map(category => (
                            <button key={category} className={`home-gallery-category-btn ${selectedCategory === category ? 'active' : ''}`} onClick={() => handleCategoryChange(category)}>
                                {category}
                            </button>
                        ))}
                    </div>
                    <div className="home-gallery-items-per-page">
                        <label htmlFor="itemsPerPageSelect">Show:</label>
                        <select id="itemsPerPageSelect" value={imagesPerPage} onChange={handleImagesPerPageChange} className="home-gallery-per-page-select">
                            <option value="8">8</option>
                            <option value="12">12</option>
                            <option value="20">20</option>
                            <option value="32">32</option>
                            <option value="48">48</option>
                        </select>
                    </div>
                </div>

                {imagesToDisplay.length > 0 ? (
                    // MODIFICATION: Replaced Masonry with a simple div for CSS Grid
                    <div className="home-gallery-grid">
                        {imagesToDisplay.map((image, index) => (
                            <div className="home-gallery-item" key={image._id || index} onClick={() => openModal(index)}>
                                <img
                                    src={image.path.startsWith('http') ? image.path : `${API_BASE_URL}${image.path}`}
                                    alt={image.filename || `Gallery image in ${image.category}`}
                                    className="home-gallery-img"
                                    loading="lazy"
                                />
                                <div className="home-gallery-item-overlay">
                                    <p>{image.filename || image.category}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="home-gallery-no-results">
                        {/* No results message remains the same */}
                        <h3>No Images Found</h3>
                        <p>
                            {selectedCategory !== 'All' ? `There are no images in the "${selectedCategory}" category.` : "There are no images in the gallery yet."}
                        </p>
                        {selectedCategory !== 'All' && (
                            <button onClick={() => handleCategoryChange('All')} className="home-gallery-button-secondary">
                                Show All Images
                            </button>
                        )}
                    </div>
                )}

                {/* Pagination remains the same */}
                {totalPages > 1 && imagesToDisplay.length > 0 && (
                    <nav className="home-gallery-pagination">
                         <ul>
                            <li>
                                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                            </li>
                            {getPageNumbers().map((page, index) => (
                                page === '...' ? (
                                    <li key={`ellipsis-${index}`} className="ellipsis"><span>...</span></li>
                                ) : (
                                    <li key={page} className={currentPage === page ? 'active' : ''}>
                                        <button onClick={() => paginate(page)}>{page}</button>
                                    </li>
                                )
                            ))}
                            <li>
                                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page">
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </li>
                        </ul>
                    </nav>
                )}
            </div>

            {/* Modal remains the same */}
            {modalImageIndex !== null && imagesToDisplay[modalImageIndex] && (
                <div className="home-gallery-modal" onClick={closeModal}>
                     <div className="home-gallery-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="home-gallery-modal-close-btn" onClick={closeModal} aria-label="Close image viewer">
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        {imagesToDisplay.length > 1 && (
                            <>
                                <button className="home-gallery-modal-nav-btn prev" onClick={showPrevImage} aria-label="Previous image">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                                <button className="home-gallery-modal-nav-btn next" onClick={showNextImage} aria-label="Next image">
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </>
                        )}
                        <figure className="home-gallery-modal-figure">
                            <img
                                src={imagesToDisplay[modalImageIndex].path.startsWith('http') ? imagesToDisplay[modalImageIndex].path : `${API_BASE_URL}${imagesToDisplay[modalImageIndex].path}`}
                                alt={imagesToDisplay[modalImageIndex].filename || `Image from ${imagesToDisplay[modalImageIndex].category}`}
                                className="home-gallery-modal-image"
                            />
                            <figcaption className="home-gallery-modal-caption">
                                <p>{imagesToDisplay[modalImageIndex].filename || imagesToDisplay[modalImageIndex].category}</p>
                            </figcaption>
                        </figure>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HomeGallery;